import { listProjects, ProjectResponse } from 'sync/project-service';
import { PerPageValue } from 'sync/query';

type GetEntireProjectListProps = {
  startPage: number;
  perPage: PerPageValue;
  organizationId: string;
};

/**
 * getEntireList fetches all databases. Only the RbacResponse of the
 * first page is kept. Subsequent pages have their data merged into the
 * RbacResponse returned by the first page.
 */
export const getEntireProjectList = async ({ startPage, perPage, organizationId }: GetEntireProjectListProps) => {
  /**
   * Fetches projects with given page and organization identifier.
   */
  const getPage = async (page: number) => {
    try {
      const result = await listProjects({ organizationId }, { page, perPage });

      return result;
    } catch {
      return null;
    }
  };

  const getNextPageRecords = async ({
    currentResponse,
    currentPage,
  }: {
    currentResponse: Awaited<ReturnType<typeof getPage>>;
    currentPage: number;
  }): Promise<ProjectResponse[]> => {
    if (!currentResponse || !currentResponse.data || currentResponse.data.length === 0) {
      return [];
    }

    const nextPage = currentPage + 1;
    const nextResponse = await getPage(nextPage);
    const nextResults = await getNextPageRecords({ currentResponse: nextResponse, currentPage: nextPage });

    return [...currentResponse.data, ...nextResults];
  };

  const startPageResponse = await getPage(startPage);

  if (!startPageResponse) {
    return null;
  }

  const nextPageRecords = await getNextPageRecords({
    currentResponse: startPageResponse,
    currentPage: startPage,
  });

  return {
    ...startPageResponse,
    data: nextPageRecords,
  };
};

export const getSearchKeySelectors = () => {
  return [
    // Add a selector to search the database name
    {
      boost: 2,
      fn: (record: ProjectResponse) => record.data.name,
    },
    // Add a selector to allow search of the individual who created
    {
      boost: 1,
      fn: (record: ProjectResponse) => {
        if (record.data.createdByUsername) {
          return record.data.createdByUsername;
        }

        return '';
      },
    },
  ] as const;
};
